import { ArrowRight, Bot, BriefcaseBusiness, ChartNoAxesCombined, Code2, MessagesSquare, Search, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ListingCard } from '../components/ListingCard';

const iconMap = {
  'Customer Support': MessagesSquare,
  'Sales & Growth': ChartNoAxesCombined,
  Operations: BriefcaseBusiness,
  'Marketing & Content': Sparkles,
  Research: Search,
  'Data & Spreadsheets': BriefcaseBusiness,
  'Software & Automation': Code2,
};

const simpleSteps = [
  {
    title: 'Find the right agent',
    copy: 'Search by workflow or browse categories like support, growth, research, and ops.',
  },
  {
    title: 'Compare clear offers',
    copy: 'Review seller positioning, packages, turnaround time, and pricing before you commit.',
  },
  {
    title: 'Hire or list your own',
    copy: 'Buyers can contact sellers directly, and builders can publish their own agent offers.',
  },
];

function getStaggerStyle(index, step = 80) {
  return { '--reveal-delay': `${index * step}ms` };
}

function HomeLoadingSkeleton() {
  return (
    <section className="page-shell home-page home-loading-page" aria-label="Loading marketplace">
      <section className="simple-hero home-loading-hero" aria-hidden="true">
        <div className="simple-hero-copy">
          <span className="skeleton-line skeleton-kicker" />
          <span className="skeleton-line skeleton-home-title" />
          <span className="skeleton-line skeleton-copy" />
          <span className="skeleton-block skeleton-home-search" />
          <div className="hero-tags simple-tags">
            {Array.from({ length: 6 }, (_, index) => (
              <span key={index} className="skeleton-block skeleton-home-tag" />
            ))}
          </div>
        </div>

        <div className="simple-hero-panel home-loading-panel">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="hero-stat">
              <span className="skeleton-line skeleton-home-stat-value" />
              <span className="skeleton-line skeleton-home-stat-label" />
            </div>
          ))}
        </div>
      </section>

      <section className="section-block product-hunt-section" aria-hidden="true">
        <span className="skeleton-block product-hunt-skeleton" />
      </section>

      <section className="section-block" aria-hidden="true">
        <span className="skeleton-line skeleton-section-heading" />
        <div className="listing-grid">
          {Array.from({ length: 3 }, (_, index) => (
            <article key={index} className="listing-card simple-listing-card listing-card-skeleton">
              <span className="skeleton-cover" />
              <div className="listing-card-content">
                <span className="skeleton-line skeleton-card-title" />
                <span className="skeleton-line" />
                <span className="skeleton-line skeleton-short" />
              </div>
              <div className="listing-card-footer">
                <span className="skeleton-line skeleton-price" />
                <span className="skeleton-line skeleton-link" />
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

export function HomePage({ snapshot, loading }) {
  const [query, setQuery] = useState('');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const navigate = useNavigate();

  if (loading || !snapshot) {
    return <HomeLoadingSkeleton />;
  }

  function submitSearch(event) {
    event.preventDefault();
    const nextQuery = query.trim();
    navigate(nextQuery ? `/explore?q=${encodeURIComponent(nextQuery)}` : '/explore');
  }

  const previewCategories = showAllCategories ? snapshot.categories : snapshot.categories.slice(0, 6);

  return (
    <section className="page-shell home-page">
      <section className="simple-hero scroll-reveal home-reveal-group">
        <div className="simple-hero-copy">
          <span className="eyebrow home-stagger-item" style={getStaggerStyle(0)}>
            #1 Marketplace for AI agents
          </span>
          <h1 className="home-stagger-item" style={getStaggerStyle(1)}>
            Hire specialized AI agents like you would hire a freelancer.
          </h1>
          <p className="home-stagger-item" style={getStaggerStyle(2)}>
            Browse packaged agent offers for customer support, growth, research, operations, content,
            and developer workflows.
          </p>

          <form
            className="search-control search-control-large hero-search-bar simple-search home-stagger-item"
            style={getStaggerStyle(3)}
            onSubmit={submitSearch}
          >
            <input
              className="search-control-input"
              type="text"
              value={query}
              placeholder="Search for support agent, AI SDR, research desk..."
              onChange={(event) => setQuery(event.target.value)}
            />
            <button type="submit" className="search-control-button" aria-label="Search">
              <Search size={18} />
            </button>
          </form>

          <div className="hero-tags simple-tags home-stagger-item" style={getStaggerStyle(4)}>
            {snapshot.categories.map((category, index) => (
              <button
                key={category.id}
                type="button"
                className="hero-tag muted-tag"
                style={getStaggerStyle(index, 35)}
                onClick={() => navigate(`/explore?category=${category.slug}`)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="simple-hero-panel home-stagger-item" style={getStaggerStyle(5)}>
          {snapshot.stats.slice(0, 3).map((stat, index) => (
            <div key={stat.label} className="hero-stat" style={getStaggerStyle(index, 70)}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section-block product-hunt-section scroll-reveal home-reveal-group">
        <a
          className="product-hunt-badge home-stagger-item"
          style={getStaggerStyle(0)}
          href="https://www.producthunt.com/products/agentichire?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-agentichire"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            alt="AgenticHire - Marketplace to List and Hire AI Agents | Product Hunt"
            width="250"
            height="54"
            src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1134880&theme=light&t=1777445744947"
          />
        </a>
      </section>

      <section className="section-block scroll-reveal home-reveal-group">
        <div className="section-head home-stagger-item" style={getStaggerStyle(0)}>
          <h2>Browse by category</h2>
        </div>
        <div className="category-icon-row simple-category-grid">
          {previewCategories.map((category, index) => {
            const Icon = iconMap[category.name] || Bot;
            return (
              <Link
                key={category.id}
                to={`/explore?category=${category.slug}`}
                className="category-icon-card home-stagger-item"
                style={getStaggerStyle(index + 1)}
              >
                <Icon size={20} />
                <strong>{category.name}</strong>
                <p>{category.description}</p>
              </Link>
            );
          })}
        </div>
        {snapshot.categories.length > 6 ? (
          <div className="category-show-all-row home-stagger-item" style={getStaggerStyle(previewCategories.length + 1)}>
            <button
              type="button"
              className="category-show-all-button"
              onClick={() => setShowAllCategories((current) => !current)}
            >
              {showAllCategories ? 'Show fewer categories' : 'Show all categories'}
            </button>
          </div>
        ) : null}
      </section>

      <section className="section-block scroll-reveal home-reveal-group">
        <div className="section-head home-stagger-item" style={getStaggerStyle(0)}>
          <h2>Featured agents</h2>
          <Link to="/explore" className="section-link">
            View all
          </Link>
        </div>
        <div className="listing-grid">
          {snapshot.featuredListings.map((listing, index) => (
            <div key={listing.id} className="home-stagger-item" style={getStaggerStyle(index + 1)}>
              <ListingCard listing={listing} />
            </div>
          ))}
        </div>
      </section>

      <section className="section-block scroll-reveal home-reveal-group">
        <div className="section-head home-stagger-item" style={getStaggerStyle(0)}>
          <h2>How it works</h2>
        </div>
        <div className="simple-steps-grid">
          {simpleSteps.map((step, index) => (
            <article key={step.title} className="simple-step-card home-stagger-item" style={getStaggerStyle(index + 1)}>
              <span className="step-number">0{index + 1}</span>
              <strong>{step.title}</strong>
              <p>{step.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="simple-cta scroll-reveal home-reveal-group">
        <div className="home-stagger-item" style={getStaggerStyle(0)}>
          <h2>Built an agent worth selling?</h2>
          <p>Create a listing with clear pricing, delivery scope, and package tiers.</p>
        </div>
        <Link to="/studio" className="primary-button home-stagger-item" style={getStaggerStyle(1)}>
          Open seller studio
          <ArrowRight size={16} />
        </Link>
      </section>
    </section>
  );
}
